import React, { useState, useEffect } from "react";
import { Modal, Button, Form, FloatingLabel } from "react-bootstrap";
import styles from "./stylesAdmin.module.css"
import { Organica, Funcionario } from "../../types/interfaces";
import { useLoading } from "../../components/LoadingContext";

interface AddOrganicaModalProps {
  show: boolean;
  onHide: () => void;
  showNotification: (message: string, variant: "success" | "danger") => void;
  onAddItem?: (item: any) => void;
}

export default function AddOrganicaModal({
  show,
  onHide,
  showNotification,
  onAddItem,
}: AddOrganicaModalProps) {
  const { setLoading } = useLoading();
  const [formData, setFormData] = useState<any>({});
  const [organicas, setOrganicas] = useState<Organica[]>([]);
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [organicasResponse, funcionariosResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/allOrganicas`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }),
          fetch(`${process.env.REACT_APP_API_URL}/api/admin/allFuncionarios`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }),
        ]);

        if (!organicasResponse.ok || !funcionariosResponse.ok) {
          throw new Error("Network response was not ok");
        }

        const [organicasData, funcionariosData] = await Promise.all([
          organicasResponse.json(),
          funcionariosResponse.json(),
        ]);

        setOrganicas(organicasData);
        setFuncionarios(funcionariosData);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    const findIdByName = (name: string, list: any[]) => {
      const selectedItem = list.find((item) => item.nombre === name);
      return selectedItem ? selectedItem.id : null;
    };

    const findIdByFuncionario = (name: string, list: any[]) => {
      const selectedItem = list.find((item) => item.funcionario === name);
      return selectedItem ? selectedItem.id : null;
    };

    setFormData((prevData: any) => {
      switch (name) {
        case "organica.nombre":
          const organicaId = findIdByName(value, organicas);
          return { ...prevData, organica: { nombre: value, id: organicaId } };
        case "funcionario.funcionario":
          const funcionarioId = findIdByFuncionario(value, funcionarios);
          return { ...prevData, funcionario: { funcionario: value, id: funcionarioId } };
        case "suborganica_parent":
          return { ...prevData, suborganica_parent: Number(value) };
        default:
          return prevData;
      }
    });
  };

  const validateForm = () => {
    const newErrors: any = {};
    let hasErrors = false;

    if (!formData.organica || !formData.organica.nombre) {
      newErrors.parent_id = "Este campo es obligatorio";
      hasErrors = true;
    }
    if (!formData.nombre) {
      newErrors.nombre = "Este campo es obligatorio";
      hasErrors = true;
    } else if (formData.nombre.length < 3) {
      newErrors.nombre = "Este campo debe tener al menos 3 caracteres";
      hasErrors = true;
    }
    if (formData.correo && !validateEmail(formData.correo)) {
      newErrors.correo = "Debe tener un formato válido (@iosfa.gob.ar)";
      hasErrors = true;
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const transformedData = {
      parent_id: formData.organica?.id || null,
      nombre: formData.nombre,
      correo: formData.correo,
      revista: formData.revista,
      descripcion: formData.descripcion,
      autoridad_funcionario_id: formData.funcionario?.id || null,
      suborganica_parent: formData.suborganica_parent || 0,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/createOrganica`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData),
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error('Failed to add organica');
      }
  
      const addedOrganica = await response.json();
  
      const organicaDetailsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/organica/${addedOrganica.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      const organicaDetails = await organicaDetailsResponse.json();
  
      showNotification("Orgánica añadida con éxito", "success");
      setFormData({});
      onHide();
      if (onAddItem) onAddItem(organicaDetails);
    } catch (error) {
      showNotification("Error al añadir orgánica", "danger");
      console.error('Error adding organica:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  return (
    <Modal show={show} onHide={onHide} className={styles.modalContainer}>
      <Form onSubmit={handleSubmit}>
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Agregar Orgánica</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
          <FloatingLabel controlId="floatingSelectParent" label="Pertenece a" className="mb-3">
            <Form.Select aria-label="Parent" name="organica.nombre" onChange={handleSelectChange} value={formData.organica?.nombre || ""}>
              <option value="" disabled>Seleccionar orgánica</option>
              {organicas.sort((a, b) => a.nombre.localeCompare(b.nombre)).map((organica) => (
                <option key={organica.id} value={organica.nombre}>{organica.nombre}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {errors.parent_id && <div className={styles.errors}>{errors.parent_id}</div>}
          <FloatingLabel controlId="floatingSelectSubOrganica" label="Es subsector" className="mb-3">
            <Form.Select aria-label="suborganica_parent" name="suborganica_parent" onChange={handleSelectChange} value={formData.suborganica_parent}>
              <option value="0">No</option>
              <option value="1">Sí</option>
            </Form.Select>
          </FloatingLabel>
          <FloatingLabel controlId="floatingInputNombre" label="Nombre" className="mb-3">
            <Form.Control type="text" placeholder="Nombre" name="nombre" onChange={handleInputChange} value={formData.nombre || ""} />
          </FloatingLabel>
          {errors.nombre && <div className={styles.errors}>{errors.nombre}</div>}
          <FloatingLabel controlId="floatingInputCorreo" label="Correo" className="mb-3">
            <Form.Control type="text" placeholder="Correo" name="correo" onChange={handleInputChange} value={formData.correo || ""} />
          </FloatingLabel>
          {errors.correo && <div className={styles.errors}>{errors.correo}</div>}
          <FloatingLabel controlId="floatingInputRevista" label="Revista" className="mb-3">
            <Form.Control type="text" placeholder="Revista" name="revista" onChange={handleInputChange} value={formData.revista || ""} />
          </FloatingLabel>
          <FloatingLabel controlId="floatingInputDescripcion" label="Descripción" className="mb-3">
            <Form.Control type="text" placeholder="Descripción" name="descripcion" onChange={handleInputChange} value={formData.descripcion || ""} />
          </FloatingLabel>
          <FloatingLabel controlId="floatingSelectAutoridad" label="Autoridad" className="mb-3">
            <Form.Select aria-label="Autoridad" name="funcionario.funcionario" onChange={handleSelectChange} value={formData.funcionario?.funcionario|| ""}>
              <option value="" disabled>Seleccionar autoridad</option>
              {funcionarios.sort((a, b) => a.funcionario.localeCompare(b.funcionario)).map((funcionario) => (
                <option key={funcionario.id} value={funcionario.funcionario}>{funcionario.funcionario}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
          <Button variant="primary" type="submit">Aceptar</Button>
          </Modal.Footer>
          </Form>
    </Modal>
  );
}
